// StudentManagement.tsx - Pure component with no internal state
import { useState, useMemo } from "react";
import {
  Users,
  Search,
  Edit2,
  Trash2,
  X,
  Mail,
  Phone,
  Calendar,
  GraduationCap,
  User,
  Save,
  UserPlus,
} from "lucide-react";
import MyButton from "@/MY_COMPONENTS/MyButton";
import { Student } from "./types";

interface StudentManagementProps {
  students: Student[];
  onStudentsChange: (students: Student[]) => void;
  isModal?: boolean;
  className?: string;
}

const EMPTY_STUDENT: Partial<Student> = {
  name: "",
  grade: "",
  section: "",
  email: "",
  phone: "",
  enrollmentDate: new Date().toISOString().split("T")[0],
  guardian: { name: "", phone: "", email: "" },
};

export default function StudentManagement({
  students,
  onStudentsChange,
  isModal = false,
  className = "",
}: StudentManagementProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState<Partial<Student>>(EMPTY_STUDENT);

  const generateId = () => `STU-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return students;
    const term = searchTerm.toLowerCase();
    return students.filter(
      s => s.name.toLowerCase().includes(term) ||
           s.grade.toLowerCase().includes(term) ||
           s.id.toLowerCase().includes(term)
    );
  }, [students, searchTerm]);

  const resetForm = () => {
    setFormData(EMPTY_STUDENT);
    setEditingStudent(null);
    setShowForm(false);
  };

  const handleSubmit = () => {
    if (!formData.name?.trim() || !formData.grade?.trim()) {
      alert("Name and Grade are required");
      return;
    }

    const student: Student = {
      id: editingStudent?.id || generateId(),
      name: formData.name.trim(),
      grade: formData.grade.trim(),
      section: formData.section?.trim() || undefined,
      email: formData.email?.trim() || undefined,
      phone: formData.phone?.trim() || undefined,
      enrollmentDate: formData.enrollmentDate || new Date().toISOString().split("T")[0],
      guardian: {
        name: formData.guardian?.name?.trim() || "",
        phone: formData.guardian?.phone?.trim() || "",
        email: formData.guardian?.email?.trim() || "",
      },
    };

    const updated = editingStudent
      ? students.map(s => s.id === student.id ? student : s)
      : [...students, student];
    
    onStudentsChange(updated);
    resetForm();
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this student?")) {
      onStudentsChange(students.filter(s => s.id !== id));
    }
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData(student);
    setShowForm(true);
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-800">Students</h3>
          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{students.length}</span>
        </div>
        <MyButton
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-indigo-600 text-white hover:bg-indigo-700 text-sm px-3 py-1.5"
          icon={<UserPlus className="w-3.5 h-3.5" />}
        >
          Add
        </MyButton>
      </div>

      {/* Search */}
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search students..."
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs uppercase">Student</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs uppercase">Grade</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 text-xs uppercase hidden md:table-cell">Contact</th>
              <th className="px-3 py-2 text-center font-medium text-gray-500 text-xs uppercase w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-400">
                  {searchTerm ? "No students match" : "No students added"}
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50 transition">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{student.name}</div>
                        <div className="text-xs text-gray-400">ID: {student.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                      {student.grade}
                    </span>
                    {student.section && <span className="ml-1 text-xs text-gray-400">({student.section})</span>}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    {student.email && <div className="text-xs text-gray-500">{student.email}</div>}
                    {student.phone && <div className="text-xs text-gray-400">{student.phone}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleEdit(student)} className="p-1 text-blue-500 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(student.id)} className="p-1 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
        Showing {filteredStudents.length} of {students.length}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{editingStudent ? "Edit Student" : "Add Student"}</h3>
              <button onClick={resetForm} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Grade *</label>
                <input
                  type="text"
                  value={formData.grade || ""}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  placeholder="e.g., 5-A"
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Section</label>
                <input
                  type="text"
                  value={formData.section || ""}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  placeholder="A"
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="student@email.com"
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ""}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+971 50 123 4567"
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Enrollment Date</label>
                <input
                  type="date"
                  value={formData.enrollmentDate || ""}
                  onChange={(e) => setFormData({ ...formData, enrollmentDate: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
              <button onClick={resetForm} className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm">
                Cancel
              </button>
              <button onClick={handleSubmit} className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm flex items-center gap-2">
                <Save className="w-3.5 h-3.5" />
                {editingStudent ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}